import React from "react";
import { useGov } from "@/hooks/governance";
import { adaFromLovelace, asPct } from "@/lib/governance";
import { Badge } from "@/components/primitives";
import { GovPageShell, GovCard, GovRow, GovError, GovSkeleton, GovEmpty } from "@/components/governance/GovShell";

const prettify = (k) =>
  k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).replace(/Dreps?/i, "DRep").replace(/Spo/i, "SPO").replace(/Pp /i, "PP ");

const ThresholdCard = ({ title, obj }) => {
  const entries = obj && typeof obj === "object" ? Object.entries(obj) : [];
  if (!entries.length) return null;
  return (
    <GovCard>
      <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mono mb-3">{title}</div>
      {entries.map(([k, v]) => (
        <GovRow key={k} label={prettify(k)}>{asPct(v)}</GovRow>
      ))}
    </GovCard>
  );
};

export const Parameters = ({ meta }) => {
  const { data: d, error, loading, refetch } = useGov("/governance/parameters");
  return (
    <GovPageShell crumbs={meta.crumbs} title={meta.title} subtitle={d ? `Active protocol parameters · epoch ${d.epoch}` : "Current protocol parameters"}>
      {error ? (
        <GovError error={error} onRetry={refetch} />
      ) : loading && !d ? (
        <GovSkeleton lines={10} />
      ) : d ? (
        <div className="grid lg:grid-cols-2 gap-4">
          <GovCard>
            <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mono mb-3">Protocol</div>
            <GovRow label="Protocol Version">{d.protocol_version ? `${d.protocol_version.major}.${d.protocol_version.minor}` : "—"}</GovRow>
            <GovRow label="Epoch">{d.epoch ?? "—"}</GovRow>
            <GovRow label="Bootstrap Phase">{d.bootstrap_phase ? <Badge tone="warn">active</Badge> : <Badge tone="success">complete</Badge>}</GovRow>
            {d.data_status ? <GovRow label="Data Status"><Badge tone={["available", "current"].includes(String(d.data_status).toLowerCase()) ? "success" : "warn"}>{d.data_status}</Badge></GovRow> : null}
          </GovCard>

          <GovCard>
            <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mono mb-3">Deposits & Lifecycle</div>
            {d.governance_actions?.deposit != null ? <GovRow label="Gov Action Deposit">{adaFromLovelace(d.governance_actions.deposit)}</GovRow> : null}
            {d.governance_actions?.lifetime_epochs != null ? <GovRow label="Action Lifetime">{d.governance_actions.lifetime_epochs} epochs</GovRow> : null}
            {d.drep?.deposit != null ? <GovRow label="DRep Deposit">{adaFromLovelace(d.drep.deposit)}</GovRow> : null}
            {d.drep?.activity_epochs != null ? <GovRow label="DRep Activity">{d.drep.activity_epochs} epochs</GovRow> : null}
            {d.committee?.min_size != null ? <GovRow label="Committee Min Size">{d.committee.min_size}</GovRow> : null}
            {d.committee?.max_term_length != null ? <GovRow label="Committee Max Term">{d.committee.max_term_length} epochs</GovRow> : null}
          </GovCard>

          <ThresholdCard title="DRep Voting Thresholds" obj={d.drep_voting_thresholds} />
          <ThresholdCard title="Pool (SPO) Voting Thresholds" obj={d.pool_voting_thresholds} />
        </div>
      ) : <GovEmpty>Parameters unavailable.</GovEmpty>}
    </GovPageShell>
  );
};
